import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Copy, Download, Upload, Check, Trash2 } from "lucide-react";
import DOMPurify from "dompurify";
import signatureService from "../services/signatureService";
import { getStoredUser } from "../utils/authUser";

const escapeHtml = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// Substitui {{token}} (com espaços opcionais) pelo valor escapado.
const render = (template, fields) =>
  (template || "").replace(/\{\{\s*(nome|cargo|email|telefone)\s*\}\}/g, (_, k) => escapeHtml(fields[k] || ""));

const Assinaturas = () => {
  // Apenas admin cria/exclui modelo (todos os analistas geram assinaturas).
  const loggedUser = getStoredUser();
  const isAdmin =
    !!loggedUser?.is_platform_admin ||
    loggedUser?.profile?.toLowerCase() === "administrador" ||
    loggedUser?.profile?.toLowerCase() === "admin";

  const [templates, setTemplates] = useState([{ id: "default", name: "Padrão Synerjet", is_default: true }]);
  const [selectedId, setSelectedId] = useState("default");
  const [template, setTemplate] = useState("");
  const [fields, setFields] = useState({ nome: "", cargo: "", telefone: "", email: "" });
  const [copied, setCopied] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [newTpl, setNewTpl] = useState(null); // {name, html}
  const fileRef = useRef(null);

  const loadList = () =>
    signatureService.listTemplates().then((d) => {
      setTemplates(Array.isArray(d?.templates) ? d.templates : []);
    }).catch(() => {});

  const loadHtml = (id) =>
    signatureService.getTemplate(id).then((d) => setTemplate(d?.html || "")).catch(() => setTemplate(""));

  useEffect(() => {
    loadList();
    loadHtml("default");
  }, []);

  const onSelectTemplate = (id) => {
    setSelectedId(id);
    loadHtml(id);
  };

  const generated = useMemo(() => render(template, fields), [template, fields]);
  const cleanPreview = useMemo(() => DOMPurify.sanitize(generated), [generated]);

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const handleCopy = async () => {
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        const item = new window.ClipboardItem({
          "text/html": new Blob([generated], { type: "text/html" }),
          "text/plain": new Blob([generated], { type: "text/plain" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(generated);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      alert("Não foi possível copiar automaticamente. Use 'Baixar .html'.");
    }
  };

  const handleDownload = () => {
    const doc = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${generated}</body></html>`;
    const blob = new Blob([doc], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const slug = (fields.nome || "assinatura").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    a.download = `assinatura-${slug || "synerjet"}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () =>
      setNewTpl({ name: file.name.replace(/\.(html?|txt)$/i, ""), html: String(reader.result || "") });
    reader.readAsText(file);
  };

  const saveTemplate = async () => {
    if (!newTpl?.html?.trim()) return;
    if (!newTpl?.name?.trim()) {
      alert("Dê um nome ao modelo (ex.: PT, GPTW-ES).");
      return;
    }
    if (!/\{\{\s*nome\s*\}\}/.test(newTpl.html)) {
      alert(
        "Este arquivo não é um MODELO válido: falta o token {{nome}}.\n\n" +
        "O modelo precisa conter {{nome}}, {{cargo}}, {{email}} e {{telefone}} onde os dados entram — " +
        "não envie uma assinatura já pronta com um nome fixo."
      );
      return;
    }
    setSavingTpl(true);
    try {
      const created = await signatureService.createTemplate(newTpl.html, newTpl.name.trim());
      setNewTpl(null);
      await loadList();
      if (created?.id) onSelectTemplate(String(created.id));
      alert("Modelo salvo. Ele já aparece no seletor de modelos para todos os analistas.");
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao salvar o modelo.");
    } finally {
      setSavingTpl(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Excluir o modelo "${t.name}"?`)) return;
    try {
      await signatureService.deleteTemplate(t.id);
      if (selectedId === String(t.id)) onSelectTemplate("default");
      await loadList();
    } catch (e) {
      alert(e.response?.data?.detail || "Erro ao excluir o modelo.");
    }
  };

  const customTemplates = templates.filter((t) => !t.is_default);
  const inputCls = "mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gerador de Assinaturas</h1>
            <p className="text-gray-500">Escolha o modelo, preencha os dados e copie a assinatura pronta.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Modelo de assinatura</label>
            <select value={selectedId} onChange={(e) => onSelectTemplate(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (padrão)" : ""}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Formulário */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nome</label>
                <input value={fields.nome} onChange={(e) => setField("nome", e.target.value)} placeholder="Ex.: Carlos Croba" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Cargo</label>
                <input value={fields.cargo} onChange={(e) => setField("cargo", e.target.value)} placeholder="Ex.: COO - Chief Operating Officer" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Telefone e ramal</label>
                <input value={fields.telefone} onChange={(e) => setField("telefone", e.target.value)} placeholder="Ex.: +55 (11) 98101-7391 | (11) 3199-0666" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">E-mail</label>
                <input value={fields.email} onChange={(e) => setField("email", e.target.value)} placeholder="Ex.: carlos.croba@synerjet.com" className={inputCls} />
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={handleCopy} disabled={!fields.nome} className="gap-2">
                  {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copiado!" : "Copiar assinatura"}
                </Button>
                <Button variant="outline" onClick={handleDownload} disabled={!fields.nome} className="gap-2 text-gray-700">
                  <Download size={16} /> Baixar .html
                </Button>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">Como aplicar no Outlook</p>
                <p className="mt-1">
                  <strong>Novo Outlook (Windows) e Outlook na web:</strong> não substitua arquivo — ele guarda a assinatura na nuvem.
                  Clique <strong>Copiar assinatura</strong> e, no Outlook, vá em <strong>Configurações → Contas → Assinaturas</strong>
                  (ou <strong>Email → Escrever e responder → Assinatura</strong>), clique no corpo e <strong>cole (Ctrl+V)</strong>. Salve.
                </p>
                <p className="mt-1">
                  <strong>Outlook clássico:</strong> também é só copiar e colar no editor de assinatura. O <strong>Baixar .html</strong>
                  serve para guardar/importar manualmente.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Pré-visualização</h2>
              <div className="overflow-x-auto rounded-md border border-gray-200 bg-white p-3">
                <div dangerouslySetInnerHTML={{ __html: cleanPreview }} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gestão de modelos (somente admin) */}
        {isAdmin && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-gray-900">Modelos (admin)</h2>
              <p className="text-sm text-gray-500">Adicione modelos nomeados (ex.: PT, GPTW-ES). Os analistas escolhem no seletor acima.</p>
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                O modelo deve usar os tokens: <code className="font-mono">{"{{nome}}"}</code>,{" "}
                <code className="font-mono">{"{{cargo}}"}</code>, <code className="font-mono">{"{{email}}"}</code>,{" "}
                <code className="font-mono">{"{{telefone}}"}</code> — eles são trocados pelos dados preenchidos.
              </div>

              {customTemplates.length > 0 && (
                <div className="mt-4 divide-y divide-gray-100">
                  {customTemplates.map((t) => (
                    <div key={t.id} className="flex items-center justify-between py-2">
                      <span className="text-sm text-gray-700">{t.name}{t.uploaded_by ? ` · ${t.uploaded_by}` : ""}</span>
                      <Button variant="outline" onClick={() => handleDelete(t)}
                        className="h-auto px-2.5 py-1 text-xs border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <input ref={fileRef} type="file" accept=".html,.htm,.txt" className="hidden" onChange={onPickFile} />
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2 text-gray-700">
                  <Upload size={16} /> Adicionar modelo (.html)
                </Button>
                {newTpl && (
                  <>
                    <input value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })}
                      placeholder="Nome do modelo (ex.: GPTW-ES)"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    <Button onClick={saveTemplate} disabled={savingTpl}>
                      {savingTpl ? "Salvando..." : "Salvar modelo"}
                    </Button>
                    <Button variant="ghost" onClick={() => setNewTpl(null)} className="text-gray-500">Cancelar</Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Assinaturas;
